/**
 * Erste Map: SHATTERED VALLEY (Masterprompt Abschnitte 34, 35).
 *
 * EINE gemeinsame Lane zwischen zwei Anchors (Produktentscheidung vom
 * 07.10.2026). Bis dahin hatte jeder Spieler seine eigene Lane, und die
 * Koordinaten galten lane-lokal aus Sicht ihres Verteidigers.
 *
 *   travelled = 0       -> das Anchor von BLAU
 *   travelled = Laenge  -> das Anchor von ORANGE
 *   y                   -> seitlicher Versatz quer zur Lane
 *
 * Es gibt nur noch EIN Koordinatensystem. Wohin eine Einheit laeuft, sagt
 * ihre Richtung (siehe getAttackDirection in @dueline/game-rules), nicht mehr
 * die Wahl der Lane.
 *
 * Alle Laengen sind TUNING_PLACEHOLDER.
 */

import type { PlayerSide, Vec2 } from '@dueline/shared';

/** Arbeitstitel der Map. Kann spaeter geaendert werden. */
export const MAP_NAME = 'Shattered Valley';
export const MAP_ID = 'shattered_valley';

/**
 * Die gemeinsame Lane von Anchor zu Anchor.
 *
 * Die Laenge folgt aus EINER Vorgabe: Wie lange dauert es, bis die beiden
 * Armeen sich treffen? Alles andere ergibt sich daraus.
 *
 * Zwei Korrekturen bis hierher:
 *
 *   422 Einheiten -> 26 s   erster Entwurf, zu lang
 *   255 Einheiten -> 15 s   im Spiel immer noch zu lang
 *   224 Einheiten -> 13 s   Rueckmeldung vom 08.10.2026
 *
 * Gerechnet wird genau: Ein Creep erscheint nicht am Anchor, sondern
 * CREEP_SPAWN_OFFSET davor. Bis zur Mitte laeuft er (Laenge / 2 - Offset).
 *
 * Was 224 Einheiten in Spielzeit heissen:
 *
 *   Creep (Tempo 8)   bis zur Mitte   13,0 s     <- die Vorgabe
 *   Creep (Tempo 6,5) bis zur Mitte   16,0 s
 *   Creep (Tempo 10)  bis zur Mitte   10,4 s
 *   Creep von Anchor zu Anchor        28 s
 *   Hero (Tempo 16)   bis zur Mitte    7,0 s   vom Anchor aus
 *   Hero (Tempo 16)   bis zur Mitte    6,1 s   von seiner Startposition
 *   Hero von Anchor zu Anchor         14 s
 *
 * Die 15 Sekunden gelten fuer ein mittleres Tempo. Eine Welle marschiert im
 * Tempo ihres LANGSAMSTEN Mitglieds - eine schwere Welle braucht also eher
 * 18 Sekunden, eine reine Laeuferwelle 12.
 *
 * Bewusst in Kauf genommen: Der Weg des Heroes ins eigene Sanctuary sinkt
 * damit auf 7 Sekunden je Richtung, also 14 Sekunden hin und zurueck. Der
 * erste Entwurf hatte 26 - der Rueckzug war dort eine teurere Entscheidung.
 * Ob 14 Sekunden genug wehtun, zeigt der Spieltest; das Sanctuary selbst
 * gibt es noch nicht.
 *
 * PUNKTSYMMETRISCH um die Mitte (107, 0): Jeder Punkt (x, y) hat sein
 * Gegenstueck (214 - x, -y).
 *
 * Warum punktsymmetrisch und nicht gespiegelt: Die Kamera des roten Spielers
 * ist um 180 Grad GEDREHT (siehe ARCHITECTURE.md). Eine Drehung des Pfades um
 * seinen Mittelpunkt ergibt deshalb fuer beide Spieler exakt dasselbe Bild.
 * Eine Spiegelung waere fuer einen von beiden seitenverkehrt - und in einem
 * 1v1 darf keine Seite die besseren Kurven haben.
 *
 * Die drei Punkte bei x = 93, 107 und 121 liegen auf einer Geraden. Das ist
 * Absicht: Dort treffen sich die Armeen zuerst, und dieses Stueck soll flach
 * und neutral sein. Der mittlere Punkt steht nur da, damit die Symmetrie in
 * den Daten sichtbar ist.
 */
export const LANE_PATH: readonly Vec2[] = [
  { x: 0, y: 0 },
  { x: 22, y: 6 },
  { x: 45, y: -5 },
  { x: 70, y: 4 },
  { x: 93, y: 0 },
  { x: 107, y: 0 },
  { x: 121, y: 0 },
  { x: 144, y: -4 },
  { x: 169, y: 5 },
  { x: 192, y: -6 },
  { x: 214, y: 0 },
];

/** Gesamtbreite der begehbaren Lane. */
export const LANE_WIDTH = 34;

/** Halbe Lane-Breite - haeufig gebrauchter Wert fuer Begrenzungspruefungen. */
export const LANE_HALF_WIDTH = LANE_WIDTH / 2;

/**
 * Wo die beiden Anchors stehen.
 *
 * Jeweils zwei Einheiten HINTER dem Pfadende - wie in der alten Lane, wo das
 * Portal bei x = 192 stand und der Pfad bei x = 190 endete. Ein Creep laeuft
 * damit bis ans Pfadende und trifft das Anchor von dort aus.
 */
export const ANCHOR_POSITION: Readonly<Record<PlayerSide, Vec2>> = {
  blue: { x: -2, y: 0 },
  orange: { x: 216, y: 0 },
};

/**
 * Wie weit vom eigenen Anchor entfernt eine Welle erscheint, auf dem Pfad
 * gemessen.
 *
 * Nicht direkt auf dem Anchor: Dort steht der Respawnpunkt des Heroes, und
 * eine Welle, die ihm auf den Kopf faellt, waere unschoen. Acht Einheiten
 * sind rund eine Creep-Sekunde.
 */
export const CREEP_SPAWN_OFFSET = 8;

/**
 * Startposition des Heroes je Seite: DIREKT VOR DEM EIGENEN ANCHOR
 * (Produktentscheidung vom 08.10.2026).
 *
 * Vorher stand der Hero 41 Einheiten davor, rund 18 Prozent der Lane. Im
 * Spiel war das eigene Portal damit beim Matchstart nicht zu sehen: Die
 * Kamera zeigt hinter dem Hero nur rund 10 Einheiten Lane. Man wusste nicht,
 * was man verteidigt.
 *
 * Jetzt sind es 12 Einheiten - das Anchor liegt beim Start sicher im Bild,
 * der Hero steht aber nicht darauf.
 *
 * Der Preis, bewusst in Kauf genommen: Der Weg zur Mitte dauert 6,1 statt
 * 4,3 Sekunden. Die Creeps brauchen 13 - der Hero ist also weiterhin lange
 * vor der ersten Welle dort.
 */
export const HERO_START_POSITION: Readonly<Record<PlayerSide, Vec2>> = {
  blue: { x: 10, y: 0 },
  orange: { x: 204, y: 0 },
};

/**
 * Punkt, an dem der Hero nach seinem Tod wieder erscheint
 * (Masterprompt Abschnitt 49).
 *
 * Ein Stueck VOR dem eigenen Anchor und deutlich seitlich versetzt:
 * - vor dem Anchor, damit der Weg zurueck in den Kampf kurz ist,
 * - seitlich AUSSERHALB der Laufspur, damit man nicht mitten in eine Welle
 *   hinein erscheint und sofort wieder stirbt.
 *
 * Der seitliche Wert ist kein Zufall: Creeps streuen beim Spawn um bis zu
 * einer halben Lane-Breite nach beiden Seiten, also rund 8 Einheiten. Der
 * Respawnpunkt liegt bewusst jenseits davon.
 *
 * Punktsymmetrisch zur Gegenseite, damit beide Spieler dasselbe Bild haben.
 *
 * Der Punkt ist fest und damit vorhersehbar - in einem kompetitiven Spiel ist
 * das wichtiger als Abwechslung.
 */
export const HERO_RESPAWN_POSITION: Readonly<Record<PlayerSide, Vec2>> = {
  blue: { x: 7, y: 13 },
  orange: { x: 207, y: -13 },
};

/**
 * Das TOR zum eigenen Sanctuary (GAME_DESIGN Abschnitt 5f).
 *
 * Bis zum 09.10.2026 war das die Mitte einer Flaeche, auf der man stand.
 * Jetzt ist es ein Durchgang: Wer hineinlaeuft, wird nach dem Channel in
 * den Raum nebenan versetzt (SANCTUARY_ROOM).
 *
 * "Nahe bzw. seitlich/hinter dem eigenen Anchor, ausserhalb des
 * Lane-Kampfbereichs."
 *
 * Die Stelle ist nicht frei gewaehlt, sondern durch drei Dinge bestimmt:
 *
 *   - ganz hinten in der eigenen Haelfte, damit der Weg dorthin wehtut,
 *   - am seitlichen Rand (y = 15 von 17), damit niemand versehentlich
 *     hineinlaeuft,
 *   - auf der GEGENUEBERLIEGENDEN Seite des Respawnpunkts. Sonst erschiene
 *     ein gefallener Hero mitten im Sanctuary und waere sofort wieder
 *     unangreifbar - das waere ein Sofort-Fluchtknopf durch die Hintertuer.
 *
 * Punktsymmetrisch zur Gegenseite (214 - x, -y), wie alles auf dieser Map.
 */
export const SANCTUARY_POSITION: Readonly<Record<PlayerSide, Vec2>> = {
  blue: { x: 5, y: -15 },
  orange: { x: 209, y: 15 },
};

/**
 * Wie weit das TOR um seine Mitte reicht.
 *
 * Klein gehalten: Creeps erscheinen CREEP_SPAWN_OFFSET vom Anchor entfernt
 * und streuen bis zu einer halben Lane-Breite zur Seite. Der naechste
 * moegliche Creep steht damit rund 8 Einheiten entfernt - das Tor reicht
 * bewusst nicht so weit.
 *
 * Seit dem 09.10.2026 ist es ein Durchgang und keine Aufenthaltsflaeche:
 * Man laeuft hinein und wird in den Raum nebenan versetzt. Deshalb darf es
 * klein sein - man soll es treffen, nicht darin wohnen.
 * TUNING_PLACEHOLDER.
 */
export const SANCTUARY_RADIUS = 5;

/**
 * Der Raum neben der Lane (Produktentscheidung vom 09.10.2026).
 *
 * Er liegt SEITLICH hinter dem Tor, ausserhalb der Lane. Dort kauft man
 * ein, heilt und geht durch dasselbe Tor zurueck.
 *
 * Warum ein eigener Ort und keine Flaeche auf der Lane: Vorher stand der
 * Hero sichtbar am Rand herum, mitten im Geschehen und doch unantastbar -
 * das sah aus wie ein Fehler. Ein Raum daneben erklaert sich von selbst.
 *
 * Die Masse sind Platzhalter: gross genug, um ein paar Schritte zu tun,
 * klein genug, dass der Weg zum Tor zurueck kurz bleibt. Punktsymmetrisch
 * zur Gegenseite (214 - x, -y), wie alles auf dieser Map.
 * TUNING_PLACEHOLDER.
 */
export const SANCTUARY_ROOM: Readonly<
  Record<PlayerSide, { minX: number; maxX: number; minY: number; maxY: number }>
> = {
  blue: { minX: -5, maxX: 15, minY: -40, maxY: -24 },
  orange: { minX: 199, maxX: 219, minY: 24, maxY: 40 },
};

/**
 * Das Tor INNERHALB des Raums - der einzige Weg zurueck.
 *
 * Es liegt an der Kante, die zur Lane zeigt, genau gegenueber dem Tor auf
 * der Lane. Wer hineinlaeuft, beginnt den Rueckweg.
 */
export const SANCTUARY_ROOM_GATE: Readonly<Record<PlayerSide, Vec2>> = {
  blue: { x: SANCTUARY_POSITION.blue.x, y: SANCTUARY_ROOM.blue.maxY },
  orange: { x: SANCTUARY_POSITION.orange.x, y: SANCTUARY_ROOM.orange.minY },
};

/**
 * Wo man im Raum ankommt.
 *
 * Ein Stueck VOM Tor weg - sonst stuende man beim Ankommen sofort wieder
 * im Rueckweg und flaechse heraus, bevor man etwas gekauft hat.
 */
export const SANCTUARY_ROOM_ENTRY: Readonly<Record<PlayerSide, Vec2>> = {
  blue: { x: SANCTUARY_ROOM_GATE.blue.x, y: SANCTUARY_ROOM_GATE.blue.y - SANCTUARY_RADIUS * 2 },
  orange: {
    x: SANCTUARY_ROOM_GATE.orange.x,
    y: SANCTUARY_ROOM_GATE.orange.y + SANCTUARY_RADIUS * 2,
  },
};

/**
 * Der gesamte begehbare Bereich der Lane.
 *
 * Gebraucht ueberall dort, wo es NICHT um die Bewegung des Heroes geht - zum
 * Beispiel beim Zielen eines Skills: Man darf ueber die eigene Haelfte hinaus
 * zielen, man darf nur nicht dorthin laufen.
 */
export const LANE_BOUNDS = {
  minX: 2,
  maxX: 212,
  minY: -LANE_HALF_WIDTH,
  maxY: LANE_HALF_WIDTH,
} as const;

/**
 * Die Kartengrenze fuer einen Hero - mehr nicht.
 *
 * Bis zum 08.10.2026 stand hier eine zweite, versteckte Grenze: Jeder Hero
 * durfte nur bis zur Mitte der Lane (x = 107). Das war ausdruecklich ein
 * PLATZHALTER "bis es die Frontline-Leash gibt".
 *
 * Im Spiel aufgefallen, nachdem es die Leash gab: "ab einer gewissen
 * Entfernung kann ich trotzdem nicht weiterlaufen, obwohl meine Armee
 * schon weiter vorne ist." Wer seinen Gegner zurueckdraengt, wurde in der
 * Mitte festgehalten - und damit gewann die Karte gegen die Armee. Das ist
 * das Gegenteil der Regel aus GAME_DESIGN Abschnitt 5a.
 *
 * Seitdem begrenzt dieser Wert nur noch das Spielfeld selbst. Wie weit
 * nach vorn jemand darf, entscheidet allein seine eigene Armee
 * (siehe leashGrenze in @dueline/game-rules).
 *
 * Je Seite bleibt es trotzdem: Die Grenze nach HINTEN ist das eigene
 * Anchor, und das liegt fuer beide woanders.
 */
export const HERO_BOUNDS: Readonly<
  Record<PlayerSide, { minX: number; maxX: number; minY: number; maxY: number }>
> = {
  blue: {
    minX: LANE_BOUNDS.minX,
    maxX: LANE_BOUNDS.maxX,
    minY: -LANE_HALF_WIDTH,
    maxY: LANE_HALF_WIDTH,
  },
  orange: {
    minX: LANE_BOUNDS.minX,
    maxX: LANE_BOUNDS.maxX,
    minY: -LANE_HALF_WIDTH,
    maxY: LANE_HALF_WIDTH,
  },
};

/**
 * Harte Obergrenze aktiver Einheiten je Spieler (Masterprompt Abschnitt 33).
 * Schuetzt die Performance auf Mobilgeraeten.
 */
export const MAX_ACTIVE_UNITS_PER_LANE = 80;
